import { ArrowLeft, FileText, Lightbulb, Loader2, Plus, RotateCcw, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { ArtifactView } from "../../components/ArtifactView";
import { composedEventFor } from "../../services/artifactEvent";
import type { SelectedContext } from "../../services/storage";
import type { CanvasSource } from "../canvas/canvasModel";
import { DOCUMENT_SECTIONS, DOCUMENT_TIP } from "../canvas/prompts";
import { SourceSelect } from "../canvas/SourcePicker";
import { useQueryableSources } from "../canvas/useQueryableSources";
import {
  isPlanning,
  MAX_SECTIONS,
  MAX_TITLE_CHARS,
  readyToCompose,
  SECTION_KINDS,
  waitingRows,
} from "./documentModel";
import { DocumentSectionRow } from "./DocumentSectionRow";
import { useDocumentCanvas } from "./useDocumentCanvas";

export type DocumentVariant = "report" | "artifact";

/** Reports and artifacts are the same document; only the wording differs. */
const VARIANTS: Record<
  DocumentVariant,
  { noun: string; back: string; nameLabel: string; untitled: string; create: string }
> = {
  report: {
    noun: "report",
    back: "Reports",
    nameLabel: "Report name",
    untitled: "Untitled report",
    create: "Create report",
  },
  artifact: {
    noun: "artifact",
    back: "Artifacts",
    nameLabel: "Artifact name",
    untitled: "Untitled artifact",
    create: "Create artifact",
  },
};

interface DocumentCanvasPageProps {
  variant: DocumentVariant;
  selectedContext: SelectedContext | null;
  onSelectContext: (context: SelectedContext) => void;
  /** Back to the workspace list, where the saved draft appears. */
  onBack: () => void;
}

/**
 * A blank document canvas: full-width sections, each a headline number,
 * chart, table, overview, key insights or summary. Creating plans every data
 * section against the chosen semantic model, then composes and saves the whole
 * document as a draft in one step.
 */
export function DocumentCanvasPage({
  variant,
  selectedContext,
  onSelectContext,
  onBack,
}: DocumentCanvasPageProps) {
  const copy = VARIANTS[variant];
  const { sources, error: sourceError, noSources, choose } = useQueryableSources(
    selectedContext,
    onSelectContext,
  );
  const [name, setName] = useState("");

  const source = useMemo<CanvasSource | null>(
    () =>
      selectedContext
        ? {
            contextId: selectedContext.contextId,
            contextVersion: selectedContext.contextVersion,
            name: selectedContext.name,
          }
        : null,
    [selectedContext],
  );
  const canvas = useDocumentCanvas(source, name.trim() || copy.untitled);
  const { rows, started, composing, draft, notice } = canvas;

  const event = useMemo(
    () => (draft ? composedEventFor(draft.artifact, draft.results) : null),
    [draft],
  );

  const locked = composing || !!draft;
  const planningNow = isPlanning(rows);
  const waiting = waitingRows(rows).length;
  const ready = readyToCompose(rows);
  const domain = selectedContext?.dataDomain ?? null;

  if (draft && event) {
    return (
      <div className="content-stack document-canvas">
        <div className="canvas-foot">
          <span role="status">Saved as a draft {copy.noun}.</span>
          <div className="canvas-foot-actions">
            <button type="button" className="button secondary small" onClick={canvas.startOver}>
              <RotateCcw size={13} /> New canvas
            </button>
            <button type="button" className="button primary small" onClick={onBack}>
              <FileText size={13} /> Open in workspace
            </button>
          </div>
        </div>
        <ArtifactView event={event} onClose={onBack} />
      </div>
    );
  }

  return (
    <div className="content-stack document-canvas">
      <header className="canvas-head">
        <button type="button" className="canvas-back" onClick={onBack}>
          <ArrowLeft size={15} /> {copy.back}
        </button>
        <div className="canvas-head-fields">
          <label className="canvas-field canvas-name">
            <span>{copy.nameLabel}</span>
            <input
              value={name}
              maxLength={MAX_TITLE_CHARS}
              placeholder={copy.untitled}
              disabled={locked}
              onChange={(change) => setName(change.target.value)}
            />
          </label>
          <SourceSelect
            sources={sources}
            value={source?.contextId ?? ""}
            disabled={started}
            onChange={choose}
          />
        </div>
        {!started && (
          <div className="canvas-head-actions">
            <button
              type="button"
              className="button primary"
              disabled={!source || noSources}
              onClick={() => void canvas.create()}
            >
              <Send size={15} /> {copy.create}
            </button>
          </div>
        )}
      </header>

      {sourceError && (
        <div className="form-error" role="alert">
          {sourceError}
        </div>
      )}
      {noSources && !sourceError && (
        <div className="inline-notice" role="status">
          No semantic model can be queried yet. Publish one before creating a {copy.noun}.
        </div>
      )}
      {notice && (
        <div className="form-error" role="alert">
          {notice}
        </div>
      )}

      <div className="document-rows">
        {rows.map((row, index) => (
          <DocumentSectionRow
            key={row.id}
            row={row}
            index={index}
            count={rows.length}
            domain={domain}
            started={started}
            locked={locked}
            onInstruction={(text) => canvas.setInstruction(row.id, text)}
            onKind={(kind) => canvas.setKind(row.id, kind)}
            onMove={(offset) => canvas.move(row.id, offset)}
            onRemove={() => canvas.remove(row.id)}
            onAsk={() => void canvas.ask(row.id)}
            onRetry={() => void canvas.retry(row.id)}
            onEdit={() => canvas.edit(row.id)}
            onChooseOption={(option) => void canvas.chooseOption(row.id, option)}
            onKeepCandidate={(index) => void canvas.keep(row.id, index)}
          />
        ))}
      </div>

      {!locked && (
        <div className="document-add" role="group" aria-label="Add section">
          <span>Add section</span>
          {SECTION_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              className="button secondary small"
              disabled={rows.length >= MAX_SECTIONS}
              onClick={() => canvas.add(kind)}
            >
              <Plus size={13} /> {DOCUMENT_SECTIONS[kind].label}
            </button>
          ))}
        </div>
      )}
      {!started && (
        <p className="canvas-tip">
          <Lightbulb size={14} aria-hidden="true" /> {DOCUMENT_TIP}
        </p>
      )}

      <footer className="canvas-foot" aria-live="polite">
        <span>
          {composing ? (
            <>
              <Loader2 size={13} className="spin" aria-hidden="true" /> Writing your {copy.noun}…
            </>
          ) : !started ? (
            `${rows.length} of ${MAX_SECTIONS} sections`
          ) : planningNow ? (
            "Finding your figures…"
          ) : waiting > 0 ? (
            `${waiting} ${waiting === 1 ? "section needs" : "sections need"} your answer · nothing saved yet`
          ) : (
            "Nothing saved yet"
          )}
        </span>
        {started && (
          <div className="canvas-foot-actions">
            <button
              type="button"
              className="button secondary small"
              onClick={canvas.startOver}
              disabled={composing}
            >
              <RotateCcw size={13} /> New canvas
            </button>
            {!composing && (
              <button
                type="button"
                className="button primary small"
                onClick={() => void canvas.compose()}
                disabled={!ready || planningNow}
                title={ready ? undefined : "Answer every section first"}
              >
                <Send size={13} /> {copy.create}
              </button>
            )}
          </div>
        )}
      </footer>
    </div>
  );
}
